import React from "react";
import { motion } from "framer-motion";
import { Section, Container, Eyebrow, RevealHeading, Reveal } from "@/components/primitives";
import { PageHero } from "@/components/PageHero";
import { ServiceGrid } from "@/components/ServiceGrid";
import { FlowDiagram } from "@/components/FlowDiagram";
import { CTASection } from "@/components/CTASection";
import { Seo } from "@/components/Seo";
import { POLITICAL } from "@/data/site";
import { PRACTICE_SERVICES } from "@/data/services";

export default function Political() {
  const p = POLITICAL;
  return (
    <>
      <Seo
        title="Political | GovX Nigeria"
        description="GovX Political helps parties, candidates and political organisations build the strategy, organisation, operations and communications required to organise and execute."
      />
      <PageHero {...p.hero} kicker="Political Operations — Neutral" />

      {/* Politics is an operating system */}
      <Section className="py-24 lg:py-32">
        <Container>
          <div className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
            <div>
              <Eyebrow>The Political Operating System</Eyebrow>
              <RevealHeading
                lines={["Politics Is", "An Operating System."]}
                as="h2"
                className="mt-7 font-display text-3xl font-medium leading-[1.05] tracking-tight sm:text-4xl"
              />
              <Reveal delay={0.1}>
                <p className="mt-6 max-w-md text-base leading-relaxed text-govx-charcoal/65">
                  Leadership, issues, organisation, communication and field operations must work
                  together. We build the machinery behind the movement.
                </p>
              </Reveal>
            </div>
            <Reveal delay={0.15}>
              <div className="border border-govx-charcoal/10 bg-white p-8 lg:p-10">
                <FlowDiagram steps={p.flow} dark={false} />
              </div>
            </Reveal>
          </div>
        </Container>
      </Section>

      {/* Services */}
      <Section dark className="py-24 lg:py-32">
        <Container>
          <Eyebrow dark>Capabilities</Eyebrow>
          <RevealHeading
            lines={["What We Do For Political Organisations"]}
            as="h2"
            className="mt-7 max-w-3xl font-display text-3xl font-medium tracking-tight sm:text-5xl"
          />
          <div className="mt-14">
            <ServiceGrid slugs={PRACTICE_SERVICES.Political} dark />
          </div>
        </Container>
      </Section>

      {/* Campaign to Government */}
      <Section className="py-24 lg:py-32">
        <Container>
          <div className="grid gap-14 lg:grid-cols-[1fr_1fr] lg:items-center">
            <div>
              <Eyebrow>Campaign → Government</Eyebrow>
              <RevealHeading
                lines={["The Work Doesn't", "End On Election Day."]}
                as="h2"
                className="mt-7 font-display text-3xl font-medium leading-[1.05] tracking-tight sm:text-4xl"
              />
              <Reveal delay={0.1}>
                <p className="mt-6 max-w-md text-base leading-relaxed text-govx-charcoal/65">
                  Political organisations often build campaign structures that disappear after an
                  election. GovX takes a different approach — we help organisations build systems
                  that evolve from campaign infrastructure into governing infrastructure.
                </p>
              </Reveal>
            </div>
            <Reveal delay={0.15}>
              <div className="border border-govx-charcoal/10 bg-white p-8 lg:p-10">
                <FlowDiagram steps={p.transition.flow} dark={false} />
              </div>
            </Reveal>
          </div>

          <div className="mt-16 grid gap-5 md:grid-cols-3">
            {p.transition.cards.map((c, i) => (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.12 }}
                className="group border border-govx-charcoal/12 bg-white p-8 transition-colors duration-500 hover:border-govx-green"
              >
                <div className="font-mono text-xs text-govx-green">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="mt-5 font-display text-2xl font-medium tracking-tight">{c.title}</h3>
                <p className="mt-3 text-sm text-govx-charcoal/60">{c.body}</p>
              </motion.div>
            ))}
          </div>
        </Container>
      </Section>

      <CTASection
        eyebrow="Political"
        title={["Build the organisation", "behind the leader."]}
        copy="Strategy, organisation, people, systems, communication and disciplined execution — designed to execute, not simply react."
        cta="Talk to GovX Political"
      />
    </>
  );
}
